import React from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  useTheme,
  Fade,
  Grow,
  Divider,
  Chip,
  Stack,
} from '@mui/material';
import {
  LocalFireDepartment,
  EmojiEvents,
  CalendarToday,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import SnowEffect from '../components/SnowEffect';

const Seasonal: React.FC = () => {
  const theme = useTheme();
  
  // Hide White Birch Holiday Hop after Dec 11th
  const showWhiteBirchEvent = dayjs().isBefore(dayjs('2025-12-12'));

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#E3F2FD', position: 'relative', overflow: 'hidden' }}>
      {/* Snow Effect */}
      <SnowEffect />
      
      {/* Hero Section */}
      <Box sx={{ 
        py: 8, 
        background: `linear-gradient(135deg, #E3F2FD 0%, ${theme.palette.secondary.main}20 100%)`,
        textAlign: 'center',
        position: 'relative',
        zIndex: 2
      }}>
        <Container maxWidth="lg">
          <Fade in timeout={1000}>
            <Box>
              <Typography
                variant="h2"
                component="h1"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  background: `linear-gradient(45deg, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 2
                }}
              >
                Holiday Specials
              </Typography>
              <Typography
                variant="h5"
                color="text.secondary"
                sx={{ maxWidth: 800, mx: 'auto', mb: 4 }}
              >
                Join us for festive fun and special holiday promotions!
              </Typography>
            </Box>
          </Fade>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8, position: 'relative', zIndex: 2 }}>
        <Grid container spacing={4}>
          {/* Holiday Bingo Card */}
          <Grid item xs={12} md={showWhiteBirchEvent ? 6 : 12}>
            <Grow in timeout={800}>
              <Card
                elevation={8}
                sx={{
                  height: '100%',
                  background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, #8B0000 100%)`,
                  color: 'white',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <EmojiEvents sx={{ fontSize: 40, mr: 2, color: 'white' }} />
                    <Typography variant="h4" component="h2" sx={{ fontWeight: 700 }}>
                      Holiday Bingo
                    </Typography>
                  </Box>
                  
                  <Divider sx={{ bgcolor: 'rgba(255,255,255,0.3)', mb: 3 }} />
                  
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, fontStyle: 'italic' }}>
                    How Does It Work?
                  </Typography>
                  <Stack spacing={1.5} sx={{ mb: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                      <Typography variant="body1" sx={{ flex: 1 }}>
                        • Cross off a space for each completed task
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                      <Typography variant="body1" sx={{ flex: 1 }}>
                        • Turn in completed bingo cards by December 31
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                      <Typography variant="body1" sx={{ flex: 1 }}>
                        • Winners drawn live on Instagram January 1
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                      <Typography variant="body1" sx={{ flex: 1 }}>
                        • For video or story - email to{' '}
                        <Box component="a" href="mailto:rose@theburnstudio.com" sx={{ color: 'white', textDecoration: 'underline' }}>
                          rose@theburnstudio.com
                        </Box>
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                      <Typography variant="body1" sx={{ flex: 1 }}>
                        • For friend squares - write your friend's name on square
                      </Typography>
                    </Box>
                  </Stack>

                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, fontStyle: 'italic' }}>
                    What Do I Win?
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    Three cards will be drawn to win one of the following:
                  </Typography>
                  <Stack spacing={1.5} sx={{ mb: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Chip label="1" size="small" sx={{ mr: 2, bgcolor: 'white', color: theme.palette.secondary.main, fontWeight: 700 }} />
                      <Typography variant="body1">One Month Unlimited Membership</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Chip label="2" size="small" sx={{ mr: 2, bgcolor: 'white', color: theme.palette.secondary.main, fontWeight: 700 }} />
                      <Typography variant="body1">The Burn Clothing Merchandise</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Chip label="3" size="small" sx={{ mr: 2, bgcolor: 'white', color: theme.palette.secondary.main, fontWeight: 700 }} />
                      <Typography variant="body1">The Burn Water Bottle</Typography>
                    </Box>
                  </Stack>

                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, fontStyle: 'italic' }}>
                    What If I Get A Blackout?
                  </Typography>
                  <Stack spacing={1.5}>
                    <Typography variant="body1">
                      • All blackout winners get invite to private Bingo Blackout Class (Date TBD)
                    </Typography>
                    <Typography variant="body1">
                      • And a free Burn T-Shirt
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            </Grow>
          </Grid>

          {/* White Birch Holiday Hop Card */}
          {showWhiteBirchEvent && (
            <Grid item xs={12} md={6}>
              <Grow in timeout={1000}>
              <Card
                elevation={8}
                sx={{
                  height: '100%',
                  background: `linear-gradient(135deg, #FFF8E7 0%, #FFE5E5 100%)`,
                  border: `3px solid ${theme.palette.secondary.main}`,
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <LocalFireDepartment sx={{ fontSize: 40, mr: 2, color: theme.palette.secondary.main }} />
                    <Typography variant="h4" component="h2" sx={{ fontWeight: 700, color: theme.palette.secondary.main }}>
                      White Birch Holiday Hop
                    </Typography>
                  </Box>
                  
                  <Divider sx={{ bgcolor: theme.palette.secondary.main, mb: 3 }} />
                  
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: theme.palette.secondary.main }}>
                    December 11th - All Day Long!
                  </Typography>
                  
                  <Typography variant="body1" sx={{ mb: 3, color: 'text.primary' }}>
                    Giveaways, in-store specials, and holiday fun across your favorite White Birch businesses here in Ankeny.
                  </Typography>

                  <Box sx={{ 
                    bgcolor: theme.palette.secondary.main, 
                    color: 'white', 
                    p: 3, 
                    borderRadius: 2, 
                    mb: 3,
                    textAlign: 'center'
                  }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                      Free Classes
                    </Typography>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      All Day!
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      2505 White Birch Dr., Ankeny
                    </Typography>
                  </Box>

                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: theme.palette.secondary.main }}>
                    Class Schedule:
                  </Typography>
                  <Stack spacing={1.5}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <CalendarToday sx={{ mr: 1, color: theme.palette.secondary.main }} />
                      <Typography variant="body1" sx={{ flex: 1 }}>5:30am - Burn Sculpt 50</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <CalendarToday sx={{ mr: 1, color: theme.palette.secondary.main }} />
                      <Typography variant="body1" sx={{ flex: 1 }}>5:45am - Burn Barre 45</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <CalendarToday sx={{ mr: 1, color: theme.palette.secondary.main }} />
                      <Typography variant="body1" sx={{ flex: 1 }}>8:30am - Burn Pilates Lift 45</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <CalendarToday sx={{ mr: 1, color: theme.palette.secondary.main }} />
                      <Typography variant="body1" sx={{ flex: 1 }}>4:30pm - Burn Pump 45</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <CalendarToday sx={{ mr: 1, color: theme.palette.secondary.main }} />
                      <Typography variant="body1" sx={{ flex: 1 }}>5:30pm - Burn Barre 50</Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
              </Grow>
            </Grid>
          )}
        </Grid>
      </Container>
    </Box>
  );
};

export default Seasonal;

